import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { ftpAPI, getTaskData } from "api";
import { useHeader } from "components/header/store";
import AddFtpItem from "./addFtpItem";
import Header from "./header";
import DeleteFtpItem from "./removeFtpItem";

/* =======================
   Types
======================= */

type PathItem = {
  where: string;
  what: string;
};

type ProjectMap = Record<string, PathItem[]>;
type FtpData = Record<string, ProjectMap>;

type Pair =
  | {
    id: string;
    type: "pair";
    from: PathItem;
    to: PathItem;
  }
  | {
    id: string;
    type: "single";
    item: PathItem;
  };

/* =======================
   Styles
======================= */

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  max-width: 1000px;
  margin: 40px auto;
  padding: 0 20px;
  font-family: Pretendard, sans-serif;
  color: #1e293b;
`;

const Group = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 16px;
  background: #fff;
`;

const GroupHeader = styled.div`
  background: #f8fafc;
  padding: 14px 18px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  border-bottom: 1px solid #e2e8f0;
`;

const ItemList = styled.div`
  padding: 16px 24px;
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 18px 0 10px;
`;

const ProjectTitle = styled.h4`
  margin: 0;
  font-size: 15px; /* 🔹 아주 조금 키움 */
  font-weight: 600;
  color: #334155;
`;

const ProjectDeleteBtn = styled.button`
  background: transparent;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #fee2e2;
  }
`;

const PairBlock = styled.div`
  margin-bottom: 12px;
`;

const PairRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 40px 1fr;
  gap: 12px;
  align-items: center;
  background: #f9fafb;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 6px;
  animation: ${fadeInUp} 0.25s ease;
`;

const PathText = styled.div<{ $type: "sftp" | "oasys" }>`
  background: ${(p) => (p.$type === "sftp" ? "#e0f2fe" : "#f1f5f9")};
  padding: 8px 10px;
  border-radius: 6px;
  font-family: "JetBrains Mono", monospace;
  font-size: 0.85rem;
  word-break: break-all;
`;

const Arrow = styled.div`
  text-align: center;
  color: #94a3b8;
`;

const AddButtonWrap = styled.div`
  margin-top: 12px;
  text-align: right;
`;

const AddButton = styled.button`
  background: #3b82f6;
  border: none;
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
`;

const Empty = styled.div`
  text-align: center;
  color: #94a3b8;
  font-size: 0.9rem;
  margin-bottom: 12px;
`;

/* =======================
   Utils
======================= */

const makePairs = (items: PathItem[]): Pair[] => {
  const used = new Set<string>();
  const map = new Map(items.map((i) => [i.where, i]));
  const result: Pair[] = [];

  for (const item of items) {
    if (used.has(item.where)) continue;

    if (item.where.endsWith("/from_oasys")) {
      const base = item.where.replace("/from_oasys", "");
      const toKey = `${base}/to_oasys`;
      const toItem = map.get(toKey);

      if (toItem) {
        used.add(item.where);
        used.add(toKey);
        result.push({
          id: base,
          type: "pair",
          from: item,
          to: toItem,
        });
        continue;
      }
    }

    if (item.where.endsWith("/to_oasys")) {
      const base = item.where.replace("/to_oasys", "");
      if (map.has(`${base}/from_oasys`)) continue;
    }

    used.add(item.where);
    result.push({
      id: item.where,
      type: "single",
      item,
    });
  }

  return result;
};

/* =======================
   Component
======================= */

const FtpPage = () => {
  const [data, setData] = useState<FtpData | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [isAddFtpItem, setIsAddFtpItem] = useState(false);
  const [isRemoveFtpItem, setIsRemoveFtpItem] = useState(false);
  const [selectUser, setSelectUser] = useState("");
  const [selectProject, setSelectProject] = useState("");

  const inFlight = useRef(false);
  const { setTitle, setButtons } = useHeader();

  useEffect(() => {
    setTitle("FTP");

    const fetch = async () => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const res = await getTaskData(ftpAPI.userDetail);
        if (res?.result?.data) {
          setData(res.result.data);
        }
      } catch (e) {
        console.error("FTP Detail Path fetch error:", e);
      } finally {
        inFlight.current = false;
      }
    };

    setButtons(
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <button
          onClick={fetch}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: "1.2rem",
            display: "flex",
            alignItems: "center",
            color: "#475569",
            paddingTop: "2px",
          }}
          title="새로고침"
        >
          ↻
        </button>
        <Header />
      </div>
    );

    fetch();
  }, [setTitle, setButtons]);

  if (!data) return null;

  const users = Object.keys(data).sort();

  return (
    <Wrapper>
      {isRemoveFtpItem && (
        <DeleteFtpItem
          open
          setOpen={setIsRemoveFtpItem}
          user={selectUser}
          project={selectProject}
        />
      )}

      {isAddFtpItem && (
        <AddFtpItem open setOpen={setIsAddFtpItem} user={selectUser} />
      )}

      {users.map((user) => {
        const projects: ProjectMap = data[user];
        const isOpen = openGroups[user];

        return (
          <Group key={user}>
            <GroupHeader
              onClick={() => setOpenGroups((p) => ({ ...p, [user]: !p[user] }))}
            >
              <span>{user}</span>
              <span>{isOpen ? "−" : "+"}</span>
            </GroupHeader>

            {isOpen && (
              <ItemList>
                {/* 🔹 프로젝트가 하나도 없을 때 */}
                {Object.keys(projects).length === 0 && (
                  <>
                    <Empty>비어 있음</Empty>
                    <AddButtonWrap>
                      <AddButton
                        onClick={() => {
                          setSelectUser(user);
                          setSelectProject("");
                          setIsAddFtpItem(true);
                        }}
                      >
                        + 항목 추가
                      </AddButton>
                    </AddButtonWrap>
                  </>
                )}

                {/* 🔹 프로젝트가 있을 때 */}
                {Object.entries(projects).map(
                  ([project, items]: [string, PathItem[]]) => {
                    const pairs = makePairs(items);

                    return (
                      <div key={project}>
                        <ProjectHeader>
                          <ProjectTitle>{project}</ProjectTitle>
                          <ProjectDeleteBtn
                            onClick={() => {
                              setSelectUser(user);
                              setSelectProject(project);
                              setIsRemoveFtpItem(true);
                            }}
                          >
                            프로젝트 삭제
                          </ProjectDeleteBtn>
                        </ProjectHeader>

                        {pairs.map((p) =>
                          p.type === "pair" ? (
                            <PairBlock key={p.id}>
                              <PairRow>
                                <PathText $type="sftp">{p.from.where}</PathText>
                                <Arrow>➡</Arrow>
                                <PathText $type="oasys">{p.from.what}</PathText>
                              </PairRow>
                              <PairRow>
                                <PathText $type="sftp">{p.to.where}</PathText>
                                <Arrow>➡</Arrow>
                                <PathText $type="oasys">{p.to.what}</PathText>
                              </PairRow>
                            </PairBlock>
                          ) : (
                            <PairRow key={p.id}>
                              <PathText $type="sftp">{p.item.where}</PathText>
                              <Arrow>➡</Arrow>
                              <PathText $type="oasys">{p.item.what}</PathText>
                            </PairRow>
                          )
                        )}

                        <AddButtonWrap>
                          <AddButton
                            onClick={() => {
                              setSelectUser(user);
                              setSelectProject(project);
                              setIsAddFtpItem(true);
                            }}
                          >
                            + 항목 추가
                          </AddButton>
                        </AddButtonWrap>
                      </div>
                    );
                  }
                )}
              </ItemList>
            )}
          </Group>
        );
      })}
    </Wrapper>
  );
};

export default FtpPage;
